import AdminTable from '../../../components/admin/AdminTable';
import React, { useState, useEffect, useCallback } from 'react';
import { Edit, Trash2, Plus, History, ShieldPlus } from 'lucide-react';
import { fetchTiposPago, TipoPago } from '../../../services/tiposPagoService';
import AddTiposPagos from '../../../components/TiposPagos/AddTiposPagos';
import EditTiposPagos from '../../../components/TiposPagos/EditTiposPagos';
import DeleteTiposPagos from '../../../components/TiposPagos/DeleteTiposPagos';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';
import { useNavigate } from 'react-router-dom';
import Loading from '../../../components/Loading';

const TiposPagosPage: React.FC = () => {
    const [tiposPago, setTiposPago] = useState<TipoPago[]>([]);
    const [loading, setLoading] = useState(true);

    const [modalAbierto, setModalAbierto] = useState(false);
    const [modalEditarAbierto, setModalEditarAbierto] = useState(false);
    const [modalEliminarAbierto, setModalEliminarAbierto] = useState(false);
    const [tipoPagoEditando, setTipoPagoEditando] = useState<TipoPago | null>(null);
    const [tipoPagoEliminando, setTipoPagoEliminando] = useState<TipoPago | null>(null);

    // Paginación
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Sesión expirada
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const navigate = useNavigate();

    const userLevelId = user?.user_level_id ?? 0;
    // Ajusta el nombre del modelo según tu backend (p.ej. 'tipopago', 'tipo_pago')
    const perm = useModelPermission('tipopago', userLevelId);

    const cargarTiposPago = useCallback(async () => {
        setLoading(true);
        try {
            if (perm?.can_read) {
                const data = await fetchTiposPago();
                const dataOrdenada = [...data].sort((a, b) => a.id - b.id);
                setTiposPago(dataOrdenada);
            } else {
                setTiposPago([]);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [perm]);

    useEffect(() => {
        if (perm === null) return;
        cargarTiposPago();
    }, [perm, cargarTiposPago]);

    const abrirModalEdicion = (tipo: TipoPago) => {
        setTipoPagoEditando(tipo);
        setModalEditarAbierto(true);
    };

    const abrirModalEliminacion = (tipo: TipoPago) => {
        setTipoPagoEliminando(tipo);
        setModalEliminarAbierto(true);
    };

    // Cálculo de qué mostrar en la página actual
    const totalPages = Math.ceil(tiposPago.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedTiposPago = tiposPago.slice(startIndex, startIndex + itemsPerPage);

    if (perm === null || (loading && tiposPago.length === 0)) {
        return <Loading text="Cargando tipos de pago..." />;
    }

    return (
        <div data-admin-page="true" className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800 admin-page-title">Gestión de <span className="admin-title-accent">Tipos de Pago</span></h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/tipos-pago" className="text-[#c1348b] hover:text-[#8f1f64]">Tipos de Pago</a>
                    </div>
                </div>

                <div className="flex gap-2 items-center">
                    {perm.can_read && (
                        <button
                            style={{ height: '39px' }}
                            className="bg-[#61257b] text-white px-4 py-2 rounded-lg hover:bg-[#4a1c5e] flex items-center text-sm font-medium transition-colors"
                            onClick={() => navigate('/tipos-pago/history')} // Ajusta la ruta si existe
                        >
                            <History className="w-5 h-5" />
                        </button>
                    )}

                    {perm.can_create && (
                        <button
                            style={{ minWidth: '170.75px', height: '39px' }}
                            className="bg-[#c1348b] text-white px-4 py-2 rounded-lg hover:bg-[#8f1f64] flex items-center text-sm font-medium transition-colors"
                            onClick={() => setModalAbierto(true)}
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Agregar Tipo de Pago
                        </button>
                    )}
                </div>
            </div>

            {/* Modales */}
            <AddTiposPagos
                isOpen={modalAbierto}
                onClose={() => setModalAbierto(false)}
                onTipoPagoCreated={cargarTiposPago}
            />
            <EditTiposPagos
                isOpen={modalEditarAbierto}
                onClose={() => setModalEditarAbierto(false)}
                onTipoPagoUpdated={cargarTiposPago}
                tipoPago={tipoPagoEditando}
            />
            <DeleteTiposPagos
                isOpen={modalEliminarAbierto}
                onClose={() => setModalEliminarAbierto(false)}
                onTipoPagoDeleted={cargarTiposPago}
                tipoPago={tipoPagoEliminando}
            />
            <ExpiredSessionModal
                isOpen={isSessionExpired}
                onClose={async () => {
                    setIsSessionExpired(false);
                    await logout();
                }}
            />

            {/* Tabla */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="text-center py-10">Cargando datos…</div>
                ) : !perm.can_read ? (
                    <div className="text-center py-12">
                        <ShieldPlus className="w-12 h-12 text-gray-400 mx-auto" />
                        <p className="text-lg font-medium text-gray-600 mt-4">
                            No tienes permiso para ver los tipos de pago
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <AdminTable className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Configuración</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha de creación</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {displayedTiposPago.length > 0 ? (
                                    displayedTiposPago.map((tipo) => (
                                        <tr key={tipo.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-4 text-sm text-gray-900 font-medium">{tipo.nombre}</td>
                                            <td className="px-4 py-4 text-sm text-gray-500">
                                                {tipo.configuracion_detail ? (
                                                    <div className="flex flex-col">
                                                        <span className="font-medium">{tipo.configuracion_detail.nombre}</span>
                                                        <span className="text-xs text-gray-400">
                                                            {tipo.configuracion_detail.tipo}
                                                            {tipo.configuracion_detail.afecta_caja && ' • Afecta caja'}
                                                            {tipo.configuracion_detail.requiere_cambio && ' • Requiere cambio'}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">Sin configuración</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">{tipo.descripcion || '-'}</td>
                                            <td className="px-4 py-4 text-sm text-gray-500">
                                                {new Date(tipo.fecha_creado).toLocaleDateString('es-ES', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    hour12: false,
                                                })}
                                            </td>
                                            <td className="px-4 py-4">
                                                {tipo.activo ? (
                                                    <span className="px-2 inline-flex text-xs font-medium rounded-full bg-green-100 text-green-800">
                                                        Activo
                                                    </span>
                                                ) : (
                                                    <span className="px-2 inline-flex text-xs font-medium rounded-full bg-red-100 text-red-800">
                                                        Inactivo
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="flex gap-2">
                                                    {perm.can_update && (
                                                        <button
                                                            onClick={() => abrirModalEdicion(tipo)}
                                                            className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-[#c1348b] hover:text-[#8f1f64] transition"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    {perm.can_delete && (
                                                        <button
                                                            onClick={() => abrirModalEliminacion(tipo)}
                                                            className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-800 transition"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-12 text-center">
                                            <div className="flex flex-col items-center justify-center space-y-3">
                                                <ShieldPlus className="w-12 h-12 text-gray-400" />
                                                <span className="text-lg font-medium text-gray-600">
                                                    No hay tipos de pago disponibles
                                                </span>
                                                {perm.can_create && (
                                                    <p className="text-gray-500 text-sm">
                                                        Agrega un nuevo tipo de pago para comenzar
                                                    </p>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </AdminTable>
                    </div>
                )}

                {/* Paginación */}
                {perm.can_read && !loading && tiposPago.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={tiposPago.length}
                    />
                )}
            </div>
        </div>
    );
};

export default TiposPagosPage;
